import { Trash2 } from "lucide-react";
import * as React from "react";

import { useAnalytics, useNavigation } from "@/providers";
import { EntityCustomFields } from "@/shared/components/CustomFields";
import {
  DetailsRail,
  DetailsRailSection,
} from "@/shared/components/DetailsRail";
import {
  UpdateDeviceTags,
  UpdateDeviceMetadata,
} from "@/shared/components/devices";
import { DeviceTypeSelect } from "@/shared/components/devices/DeviceTypeSelect";
import {
  folderOf,
  UrdfPreviewPanel,
} from "@/shared/components/devices/UrdfPreviewPanel";
import { FactList } from "@/shared/components/FactList";
import { RobotoPrincipalUnderlineLink } from "@/shared/components/principal";
import { FormattedDateTime } from "@/shared/datetime";
import { TargetEntityType } from "@/shared/domain/customFields";
import { useDevice } from "@/shared/domain/devices/hooks/useDevice";
import { useUpdateDevice } from "@/shared/domain/devices/hooks/useUpdateDevice";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import type { FileRecord } from "@/shared/domain/files";
import { Callout, RobotoButton, Spinner } from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http";
import { Goto } from "@/shared/navigation";

import { useDeviceContext } from "../Device/useDeviceContext";
import { INHERITED_FOLDER_PARAM } from "../DeviceConfigurations";

import { DeleteDeviceDialog } from "./DeleteDeviceDialog";
import styles from "./DeviceDetails.module.css";

/**
 * Details tab of the device-detail route: a Properties card of the device's
 * IDs, its Type picker (when `release-asset-store` is on), inline editors for
 * tags and metadata, and who created and last modified it; then the custom
 * fields card and an Operations card with Delete. A column to the right
 * previews the device's URDF.
 */
export function DeviceDetails() {
  const { goto } = useNavigation();
  const { trackEvent } = useAnalytics();
  const { deviceId, org } = useDeviceContext();
  const currentOrgId = org?.org_id;
  const deviceQuery = useDevice(deviceId, currentOrgId);
  const { mutateAsync: updateDevice } = useUpdateDevice();
  // A separate mutation from the custom-fields editor's, so a custom-field save
  // neither disables the Type picker nor shows its error there.
  const deviceTypeUpdate = useUpdateDevice();
  const assetStoreEnabled = useCheckFeatureFlag(
    "release-asset-store",
    currentOrgId,
  );
  const [customFieldsError, setCustomFieldsError] =
    React.useState<Error | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const typeLabelId = React.useId();
  // `release-3d-assets` gates URDF ingestion, so without it there is no URDF
  // to preview.
  const previewsEnabled = useCheckFeatureFlag(
    "release-3d-assets",
    currentOrgId,
  );

  const device = deviceQuery.data;
  const deviceTypeId = assetStoreEnabled ? device?.device_type_id : undefined;

  // Links to the folder holding a URDF on the Configurations tab, in the
  // section that lists it. A folder of the device's own files goes in the URL
  // path; a folder inherited from the device's type goes in the
  // `inheritedFolder` search parameter.
  const urdfFolderHref = (urdf: FileRecord): string => {
    const folder = folderOf(urdf.relative_path);
    if (urdf.association_id !== deviceTypeId) {
      return Goto.createDeviceConfigurationsRoute(deviceId, folder);
    }
    const root = Goto.createDeviceConfigurationsRoute(deviceId);
    return folder
      ? `${root}?${new URLSearchParams({ [INHERITED_FOLDER_PARAM]: folder }).toString()}`
      : root;
  };

  if (deviceQuery.error) {
    return <Callout variant="error">{deviceQuery.error.message}</Callout>;
  }

  if (!device) {
    return (
      <p className={styles.loading}>
        <Spinner aria-hidden />
        Loading device&hellip;
      </p>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.main}>
        {customFieldsError && (
          <Callout variant="error">
            {userFacingErrorMessage(
              customFieldsError,
              "Failed to update custom fields.",
            )}
          </Callout>
        )}

        <section className={styles.card}>
          <h3 className={styles.cardHeading}>Properties</h3>
          <FactList divided className={styles.properties}>
            <FactList.Item label="Device ID" mono>
              {device.device_id}
            </FactList.Item>

            {device.universal_device_id && (
              <FactList.Item label="Roboto Device ID" mono>
                {device.universal_device_id}
              </FactList.Item>
            )}

            {assetStoreEnabled && (
              <FactList.Item label={<span id={typeLabelId}>Type</span>}>
                <div className={styles.typeSelect}>
                  <DeviceTypeSelect
                    aria-labelledby={typeLabelId}
                    orgId={device.org_id}
                    value={device.device_type_id ?? null}
                    disabled={deviceTypeUpdate.isPending}
                    onChange={(deviceTypeId) =>
                      deviceTypeUpdate.mutate({
                        deviceId,
                        resourceOwnerId: device.org_id,
                        updates: { device_type_id: deviceTypeId },
                      })
                    }
                  />
                  {deviceTypeUpdate.error && (
                    <Callout variant="error">
                      {userFacingErrorMessage(deviceTypeUpdate.error)}
                    </Callout>
                  )}
                </div>
              </FactList.Item>
            )}

            <FactList.Item label="Tags">
              {currentOrgId ? (
                <UpdateDeviceTags
                  device={device}
                  resourceOwnerId={currentOrgId}
                />
              ) : (
                "Loading..."
              )}
            </FactList.Item>

            <FactList.Item label="Metadata">
              {currentOrgId ? (
                <UpdateDeviceMetadata
                  device={device}
                  resourceOwnerId={currentOrgId}
                />
              ) : (
                "Loading..."
              )}
            </FactList.Item>

            <FactList.Item label="Created">
              <FormattedDateTime value={device.created} />
              {" by "}
              <RobotoPrincipalUnderlineLink
                className={styles.inlinePrincipal}
                principal={device.created_by}
              />
            </FactList.Item>

            <FactList.Item label="Updated">
              <FormattedDateTime value={device.modified} />
              {" by "}
              <RobotoPrincipalUnderlineLink
                className={styles.inlinePrincipal}
                principal={device.modified_by}
              />
            </FactList.Item>
          </FactList>
        </section>

        <EntityCustomFields
          entityType={TargetEntityType.Device}
          orgId={device.org_id}
          values={device.custom_fields}
          commit={(changeset) =>
            updateDevice({
              deviceId,
              resourceOwnerId: device.org_id,
              updates: { custom_fields_changeset: changeset },
            })
          }
          onError={setCustomFieldsError}
          variant="card"
        />

        <section className={styles.card}>
          <h3 className={styles.cardHeading}>Operations</h3>
          <div className={styles.operations}>
            <RobotoButton
              variant="destructive-secondary"
              icon={<Trash2 aria-hidden="true" />}
              onClick={() => {
                trackEvent("DeleteDeviceClicked", { deviceId });
                setDeleteDialogOpen(true);
              }}
            >
              Delete device
            </RobotoButton>
          </div>
        </section>
      </div>

      {previewsEnabled && assetStoreEnabled && device.universal_device_id && (
        <DetailsRail>
          <DetailsRailSection label="URDF">
            <UrdfPreviewPanel
              associationId={device.universal_device_id}
              inheritedAssociationId={deviceTypeId ?? undefined}
              orgId={device.org_id}
              owner="device"
              folderHref={urdfFolderHref}
            />
          </DetailsRailSection>
        </DetailsRail>
      )}

      <DeleteDeviceDialog
        devices={deleteDialogOpen ? [device] : []}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={() => {
          setDeleteDialogOpen(false);
          goto.devices();
        }}
      />
    </div>
  );
}
